import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { STRIPE_ONBOARDING_TOKEN_KEY } from '../auth/pages/register/stripe-register-entry/stripe-onboarding.constants';
import { AuthService } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';
import { TokenRefreshService } from '../services/token-refresh.service';

/**
 * Unified token interceptor that handles both token attachment and refresh logic.
 * This interceptor:
 * 1. Attaches the Bearer token to API requests
 * 2. Handles 401 errors by refreshing the token
 * 3. Queues requests during token refresh to prevent race conditions
 * 4. Retries failed requests with the new token
 */
export const tokenInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const tokenRefreshService = inject(TokenRefreshService);
  const authService = inject(AuthService);

  // Check if this is an API request that needs authentication
  const isApiUrl = req.url.startsWith(ConfigService.getConfig().api);
  const isAnonymousUrl =
    req.url.toLowerCase().includes('/authentication/') ||
    req.url.toLowerCase().includes('/auth/login') ||
    req.url.toLowerCase().includes('/auth/register');

  // Skip token handling for non-API or anonymous requests
  if (isAnonymousUrl || !isApiUrl) {
    return next(req);
  }

  // CompleteStripeRegistration requires the short-lived onboarding JWT from sessionStorage
  const stripeOnboardingToken = req.url.includes('CompleteStripeRegistration') ? sessionStorage.getItem(STRIPE_ONBOARDING_TOKEN_KEY) : null;
  const token$ = stripeOnboardingToken ? of(stripeOnboardingToken) : tokenRefreshService.getAccessToken();

  // Get the access token (with refresh if needed)
  return token$.pipe(
    switchMap((token) => {
      // Clone the request with the token if available
      const authenticatedReq = token
        ? req.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
          })
        : req;

      return next(authenticatedReq);
    }),
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized errors
      if (error.status === 401) {
        // Check if this is not a refresh token request to avoid infinite loops
        if (!req.url.includes('/Authentication/token/')) {
          console.log(`Authentication error (401) for ${req.url}, attempting token refresh...`);

          // Try to refresh the token - this will wait if a refresh is already in progress
          return tokenRefreshService.getAccessToken().pipe(
            switchMap((newToken) => {
              if (newToken) {
                console.log('Token refresh succeeded, retrying original request');
                // Clone the request with the new token
                const retryReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${newToken}` }
                });
                return next(retryReq);
              } else {
                console.log('Token refresh failed, redirecting to login');
                authService.handleAuthenticationFailure();
                if (!router.url.includes('confirmEmailChange')) {
                  router.navigate(['/auth/login']);
                }
                return throwError(() => error);
              }
            }),
            catchError((retryError) => {
              // If the retry also fails with 401, the token refresh didn't work
              if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
                console.log('Retry request also failed with 401, redirecting to login');
                authService.handleAuthenticationFailure();
                if (!router.url.includes('confirmEmailChange')) {
                  router.navigate(['/auth/login']);
                }
              } else {
                console.log('Retry request failed with error, redirecting to login', retryError);
                authService.handleAuthenticationFailure();
                if (!router.url.includes('confirmEmailChange')) {
                  router.navigate(['/auth/login']);
                }
              }
              return throwError(() => retryError);
            })
          );
        }
      }

      // For other errors, just pass them through
      return throwError(() => error);
    })
  );
};
